/**
 * ⏱️ Timer Event Reward System API Client for Zeta Admin
 * Specifications from TIMER_ADMIN_DOCUMENTATION.md
 */

const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:18009/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  if (!token) {
    return { "Content-Type": "application/json" };
  }
  const authHeader =
    token.startsWith("Basic ") || token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  return {
    "Content-Type": "application/json",
    Authorization: authHeader,
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message ||
          result?.error ||
          result?.sqlMessage ||
          result?.message ||
          result?.details)) ||
      (typeof result === "string" ? result : "API request failed");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * 1. Get Timer Settings & Lifetime Statistics
 * Endpoint: GET /admin/timer/settings
 */
export const getTimerSettings = async () => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/timer/settings`,
    `${baseUrl}/timer`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error fetching timer settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * 2. Update Timer Settings
 * Endpoint: PUT /admin/timer/settings
 * @param {Object} settingsData
 * @param {number} settingsData.timerSeconds
 * @param {number} settingsData.eventPoints
 * @param {number} settingsData.cooldownSeconds
 * @param {boolean} settingsData.isActive
 */
export const updateTimerSettings = async (settingsData = {}) => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/timer/settings`,
    `${baseUrl}/timer`,
  ];

  const payload = {
    timerSeconds: Number(settingsData.timerSeconds ?? 30),
    eventPoints: Number(settingsData.eventPoints ?? 50),
    cooldownSeconds: Number(settingsData.cooldownSeconds ?? 0),
    isActive: Boolean(settingsData.isActive),
  };

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error updating timer settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * 3. Set Timing & Event Points (Start Config API)
 * Sets global active timer parameters and optionally initializes a session for a user.
 * Endpoint: POST /admin/timer/start
 * @param {Object} configData
 * @param {number} configData.timerSeconds
 * @param {number} configData.eventPoints
 * @param {number} configData.cooldownSeconds
 * @param {boolean} configData.isActive
 * @param {number|string} [configData.userId] - Optional
 */
export const startTimerConfig = async (configData = {}) => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/timer/start`,
    `${baseUrl}/timer`,
  ];

  const payload = {
    timerSeconds: Number(configData.timerSeconds ?? 30),
    eventPoints: Number(configData.eventPoints ?? 50),
    cooldownSeconds: Number(configData.cooldownSeconds ?? 0),
    isActive: Boolean(configData.isActive),
  };

  if (configData.userId !== undefined && configData.userId !== null && configData.userId !== "") {
    payload.userId = Number(configData.userId);
  }

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error starting timer config");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * 4. Get Timer Session Audit History Logs
 * Endpoint: GET /admin/timer/history
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @param {string} [params.search='']
 * @param {string} [params.status='all']
 * @param {number|string} [params.userId]
 * @param {string} [params.eventId]
 */
export const getTimerHistory = async ({
  page = 1,
  limit = 20,
  perPage = 20,
  search = "",
  status = "all",
  userId = "",
  eventId = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  const activeLimit = limit || perPage || 20;
  if (page) queryParams.append("page", String(page));
  if (activeLimit) {
    queryParams.append("limit", String(activeLimit));
    queryParams.append("perPage", String(activeLimit));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());
  if (userId && String(userId).trim() && userId !== "all") {
    queryParams.append("userId", String(userId).trim());
  }
  if (eventId && String(eventId).trim() && eventId !== "all") {
    queryParams.append("eventId", String(eventId).trim());
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const endpoints = [
    `${baseUrl}/timer/history${queryString}`,
    `${baseUrl}/timer/logs${queryString}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error fetching timer history");
      }
    }
  }

  return handleApiResponse(lastResponse);
};
